import { DialogPaperSlotPropsOverrides, Paper, PaperProps, Slide, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { JSXElementConstructor, forwardRef, useEffect, useRef, useState } from 'react';
import Draggable, { ControlPosition } from 'react-draggable';

type ClickableEventTarget = EventTarget & {
  click?: () => void;
};

/**
 * React-Draggable container around the MUI Paper element used for creating moveable containers. IE: Modal/Dialogs https://mui.com/material-ui/react-dialog
 */
function DraggablePaper({
  fullScreenOverride,
  // Intentionally deconstructing ownerState out of the props
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ownerState,
  ...props
}: PaperProps & DialogPaperSlotPropsOverrides) {
  /**
   * Passing the ref to the underlying draggable node to resolve a Strict Mode bug.
   * https://stackoverflow.com/questions/63603902/finddomnode-is-deprecated-in-strictmode-finddomnode-was-passed-an-instance-of-d
   */
  const ref = useRef<HTMLDivElement>(null);

  const [dragPosition, setDragPosition] = useState<ControlPosition>({
    x: 0,
    y: 0
  });

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  useEffect(() => {
    // When the screen width is going to mobile, dragging is disabled and the modal is full screen.
    // We need to reset to the correct position to center the fullscreen modal correctly.
    if (isMobile || fullScreenOverride) {
      setDragPosition({ x: 0, y: 0 });
    }
  }, [isMobile, fullScreenOverride]);

  return (
    <Draggable
      handle={`#draggable-window`}
      cancel={'[class*="MuiDialogContent-root"]'}
      nodeRef={ref}
      disabled={isMobile || fullScreenOverride}
      onStop={(event, data) => {
        const { target, type } = event;
        setDragPosition({ x: data.x, y: data.y });
        /**
         * This is to allow buttons on the draggable container to be clicked in mobile
         * https://github.com/react-grid-layout/react-draggable/issues/597
         */
        if (type === 'touchend' && target) {
          const clickable = target as ClickableEventTarget;
          if (clickable.click) clickable.click();
        }
      }}
      position={dragPosition}
      offsetParent={document.body}>
      <Paper
        {...props}
        ref={ref}
      />
    </Draggable>
  );
}

/**
 * MUI Transition that can be passed in to various MUI Components "TransitionComponent" prop to alter the transition style.
 */
const SlideTransition = forwardRef(function Transition(
  props: {
    children: React.ReactElement<unknown, string | JSXElementConstructor<unknown>>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction='up'
      ref={ref}
      {...props}
    />
  );
});

export { DraggablePaper, SlideTransition };
